import { lerpAngle } from '../core/math';

export interface Sample {
  /** Local-clock time in seconds. */
  t: number;
  x: number;
  z: number;
  yaw: number;
  speed: number;
  height: number;
}

/**
 * Maps one peer's clock onto the local clock (research 04 §4.5). The offset
 * tracks the least-delayed packet seen, so network jitter is removed from
 * everything that peer sends (snapshots and timed reliable messages alike);
 * it may creep upward slowly to follow clock skew or a lasting route change.
 */
export class PeerClock {
  private offset: number | null = null;

  /** Records a packet sent at `senderMs` (sender clock) that arrived at `nowS` (local seconds); returns its local time. */
  observe(senderMs: number, nowS: number): number {
    const off = nowS - senderMs / 1000;
    if (this.offset === null || off < this.offset) this.offset = off;
    else this.offset += (off - this.offset) * 0.002;
    return senderMs / 1000 + this.offset;
  }

  /** Local time of a sender timestamp (without refining the estimate). */
  toLocal(senderMs: number, nowS: number): number {
    return this.offset === null ? nowS : senderMs / 1000 + this.offset;
  }
}

/** Distance between two consecutive samples beyond which motion snaps (blinks, respawns). */
const SNAP_DISTANCE = 6;

/**
 * Snapshot interpolation buffer. Samples are placed on the local timeline by a
 * `PeerClock`; `sample(renderTime)` interpolates between the two samples
 * around the render time, or extrapolates up to 100 ms past the newest one.
 */
export class InterpBuffer {
  private samples: Sample[] = [];

  push(t: number, s: Omit<Sample, 't'>): void {
    const last = this.samples[this.samples.length - 1];
    if (last && t <= last.t) return; // out of order (unreliable channel)
    this.samples.push({ t, ...s });
    if (this.samples.length > 32) this.samples.shift();
  }

  /** State at local time `t` (interpolated, or extrapolated up to 100 ms). */
  sample(t: number): Sample | null {
    const b = this.samples;
    if (b.length === 0) return null;
    const first = b[0] as Sample;
    if (t <= first.t) return first;
    for (let i = 1; i < b.length; i++) {
      const a = b[i - 1] as Sample;
      const c = b[i] as Sample;
      if (t <= c.t) {
        if (Math.hypot(c.x - a.x, c.z - a.z) > SNAP_DISTANCE) return { ...c, t };
        const k = (t - a.t) / Math.max(1e-6, c.t - a.t);
        return { t, x: a.x + (c.x - a.x) * k, z: a.z + (c.z - a.z) * k, yaw: lerpAngle(a.yaw, c.yaw, k), speed: c.speed, height: a.height + (c.height - a.height) * k };
      }
    }
    const l = b[b.length - 1] as Sample;
    const dt = Math.min(t - l.t, 0.1);
    return { ...l, t, x: l.x + Math.sin(l.yaw) * l.speed * dt, z: l.z + Math.cos(l.yaw) * l.speed * dt };
  }

  get latest(): Sample | null {
    return this.samples[this.samples.length - 1] ?? null;
  }

  clear(): void {
    this.samples = [];
  }
}

/** Interpolation delay in seconds for a send rate (2 intervals + margin). */
export const interpDelay = (sendHz: number): number => Math.min(0.25, Math.max(0.066, 2 / sendHz + 0.03));
